<template>
    <div id="asset-detail-component">
        <div v-if="!fetching_configuration && is_asset_detail_visible">
            <container
                v-for="n in widgets.length + (is_mobile ? 0 : 1)"
                :key="n"
                :get-child-payload="getWidget(n - 1)"
                :orientation="is_mobile ? 'vertical' : 'horizontal'"
                class="w-100 table-fixed"
                group-name="1"
                non-drag-area-selector=".no-drag"
                @drop="onDrop(n - 1, $event)"
            >
                <draggable
                    v-for="(widget, i) in widgets[n - 1]"
                    :key="i"
                    :style="`width: ${100 / (is_mobile ? 1 : widgets[n - 1].length)}% !important;`"
                    class="no-drag"
                >
                    <div class="ma-2">
                        <template v-if="widget.widget_name === 'alerts_configured_widget'">
                            <AlertsConfiguredWidgetComponent :asset="asset" :asset_module="asset_module" />
                        </template>

                        <template v-else-if="widget.widget_name === 'alerts_triggered_widget'">
                            <AlertsWidgetComponent :asset="asset" :asset_module="asset_module" />
                        </template>

                        <template v-else-if="widget.widget_name === 'asset_additional_fields_widget'">
                            <AssetAdditionalFieldsWidgetComponent :asset="asset" :asset_module="asset_module" />
                        </template>

                        <template v-else-if="widget.widget_name === 'asset_devices_information_widget'">
                            <AssetDevicesInformationWidgetComponent :devices="asset.devices" :asset_module="asset_module" />
                        </template>

                        <template v-else-if="widget.widget_name === 'asset_localisation_widget'">
                            <AssetLocalisationWidgetComponent :asset="asset" :asset_module="asset_module" />
                        </template>

                        <template v-else-if="widget.widget_name === 'asset_model_specifications_widget'">
                            <AssetModelSpecificationsWidgetComponent :asset="asset" />
                        </template>

                        <template v-else-if="widget.widget_name === 'biodigestor_historique_widget'">
                            <BiodigestorHistoryDatatableWidgetComponent :device="asset.devices[0]" :asset="asset" />
                        </template>

                        <template v-else-if="widget.widget_name === 'biodigestor_general_vision'">
                            <BiodigestorGeneralViewWidgetComponent :device="asset.devices[0]" :asset="asset" :asset-module="asset_module" />
                        </template>

                        <template v-else-if="widget.widget_name === 'biodigestor_monitoring_widget'">
                            <BiodigestorMonitoringWidgetComponent :device="asset.devices[0]" :asset="asset" />
                        </template>

                        <template v-else-if="widget.widget_name === 'biodigestor_cycles_widget'">
                            <BiodigestorCyclesWidgetComponent :device="asset.devices[0]" :asset="asset" />
                        </template>

                        <template v-else-if="widget.widget_name === 'asset_operations_datatable_widget'">
                            <AssetOperationsDatatableWidgetComponent :asset="asset" :asset_module="asset_module" />
                        </template>

                        <template v-else-if="widget.widget_name.includes('chart_by_asset_metric_widget')">
                            <ChartByAssetWidgetComponent
                                :asset="asset"
                                :chart_configuration="charts_configuration.find((config) => widget.widget_name.includes(config.widget_key))"
                                :device="getDeviceByChartConfigurationAndWidget(widget)"
                                previsional_label_key="boostherm_widget_component_prevision"
                            />
                        </template>

                        <template v-else-if="widget.widget_name === 'chart_by_metrics_widget'">
                            <ChartByMetricsWidgetComponent :asset="asset" :asset_module="asset_module" :device="asset.devices[0]" />
                        </template>

                        <template v-else-if="widget.widget_name === 'consumption_histogram_chart_widget'">
                            <ConsumptionHistogramChartWidgetComponent
                                :asset="asset"
                                :asset_module="asset_module"
                                :device="asset.devices[0]"
                            />
                        </template>

                        <template v-else-if="widget.widget_name === 'consumption_report_widget'">
                            <ConsumptionReportWidgetComponent
                                :asset="asset"
                                :asset_module="asset_module"
                                :device="asset.devices[0]"
                                :configuration_widget="widget.configuration"
                            />
                        </template>

                        <template v-else-if="widget.widget_name.includes('device_traited_data_by_asset_metric_datatable_widget')">
                            <DeviceTraitedDataByAssetMetricDatatableWidgetComponent
                                :asset="asset"
                                :device="getDeviceByMetricsConfigurationAndWidget(widget)"
                                :metrics_configuration="
                                    metrics_configuration.find((config) => widget.widget_name.includes(config.widget_key))
                                "
                            />
                        </template>

                        <template v-else-if="widget.widget_name === 'device_traited_data_by_device_metric_datatable_widget'">
                            <DeviceTraitedDataByDeviceMetricDatatableWidgetComponent :device="asset.devices[0]" />
                        </template>

                        <template v-else-if="widget.widget_name === 'donut_chart_widget'">
                            <DonutChartWidgetComponent
                                :asset_module="asset_module"
                                :configuration_widget="widget.configuration"
                                :device="asset?.devices[0]"
                            />
                        </template>

                        <template v-else-if="widget.widget_name === 'image_asset_widget'">
                            <ImageAssetWidgetComponent :asset="asset" :asset_module="asset_module" />
                        </template>

                        <template v-else-if="widget.widget_name === 'information_asset_widget'">
                            <BoosthermWidgetComponent
                                v-if="asset_module.id_module === 54 || asset_module.id_module === 64"
                                :asset="asset"
                            />

                            <InformationAssetWidgetComponent v-else :asset="asset" :asset_module="asset_module" />
                        </template>

                        <template v-else-if="widget.widget_name === 'economy_co2_widget'">
                            <EconomyCo2WidgetComponent :id_asset="asset.id_asset" :is_open="true" />
                        </template>

                        <template v-else-if="widget.widget_name === 'information_hive_widget'">
                            <HiveInformationWidget :asset="asset" :asset_module="asset_module" />
                        </template>

                        <template v-else-if="widget.widget_name === 'map_position_history_widget'">
                            <MapPositionHistoryWidgetComponent :device="getDeviceMapPositionHistoryWidget()" />
                        </template>

                        <template v-else-if="widget.widget_name === 'note_devices_widget'">
                            <NoteDevicesWidgetComponent :devices="asset.devices" />
                        </template>

                        <template v-else-if="widget.widget_name === 'public_page_widget'">
                            <PublicPageWidgetComponent :device="asset.devices[0]" />
                        </template>

                        <template v-else-if="widget.widget_name === 'apiary_history_widget'">
                            <HiveHistoryWidgetComponent :id-device="asset.devices[0].id_device" />
                        </template>

                        <template v-else-if="widget.widget_name === 'hive_chart_widget'">
                            <HiveChartWidgetComponent :id-device="asset.devices[0].id_device" />
                        </template>

                        <template v-else-if="widget.widget_name === 'follow_up_timeline_widget'">
                            <FollowUpTimelineWidgetComponent
                                :has-right-to-edit="userHasAccess(asset_module.designation, role_edit)"
                                :id-relatable="asset.id_asset"
                                :title="$t('follow_up_timeline_widget_component.follow_up_equipment')"
                                :type-relatable="NoteTypeRelatable.ASSETS"
                                :asset="asset"
                                :asset_module="asset_module"
                            />
                        </template>

                        <template v-else-if="widget.widget_name === 'biodigestor_information_equipment'">
                            <BiodigestorAssetSummaryCardWidgetComponent :asset="asset" />
                        </template>
                    </div>
                </draggable>
            </container>
        </div>

        <template v-else>
            <v-row class="mt-8">
                <v-col v-for="n in 4" :key="n" cols="12" lg="6">
                    <v-skeleton-loader type="card-heading, list-item-two-line, image, table-thead" />
                </v-col>
            </v-row>
        </template>
    </div>
</template>

<script>
import { Container, Draggable } from 'vue-smooth-dnd'
import ImageAssetWidgetComponent from '@/components/equipment/generic/widget/ImageAssetWidgetComponent.vue'
import InformationAssetWidgetComponent from '@/components/equipment/generic/widget/InformationAssetWidgetComponent.vue'
import DeviceRepository from '@/repositories/DeviceRepository'
import ChartByAssetWidgetComponent from '@/components/equipment/generic/widget/ChartByAssetWidgetComponent.vue'
import BoosthermWidgetComponent from '@/components/equipment/generic/widget/BoosthermWidgetComponent.vue'
import AssetLocalisationWidgetComponent from '@/components/equipment/generic/widget/AssetLocalisationWidgetComponent.vue'
import AssetDevicesInformationWidgetComponent from '@/components/equipment/generic/widget/AssetDevicesInformationWidgetComponent.vue'
import AssetModelSpecificationsWidgetComponent from '@/components/equipment/generic/widget/AssetModelSpecificationsWidgetComponent.vue'
import ChartByMetricsWidgetComponent from '@/components/equipment/generic/widget/ChartByMetricsWidgetComponent.vue'
import DeviceTraitedDataByAssetMetricDatatableWidgetComponent from '@/components/equipment/generic/widget/DeviceTraitedDataByAssetMetricDatatableWidgetComponent.vue'
import DeviceTraitedDataByDeviceMetricDatatableWidgetComponent from '@/components/equipment/generic/widget/DeviceTraitedDataByDeviceMetricDatatableWidgetComponent.vue'
import MapPositionHistoryWidgetComponent from '@/components/equipment/generic/widget/MapPositionHistoryWidgetComponent.vue'
import AlertsConfiguredWidgetComponent from '@/components/equipment/generic/widget/AlertsConfiguredWidgetComponent.vue'
import ConsumptionReportWidgetComponent from '@/components/equipment/generic/widget/ConsumptionReportWidgetComponent.vue'
import AlertsWidgetComponent from '@/components/equipment/generic/widget/AlertsWidgetComponent.vue'
import NoteDevicesWidgetComponent from '@/components/equipment/generic/widget/NoteDevicesWidgetComponent.vue'
import AssetAdditionalFieldsWidgetComponent from '@/components/equipment/generic/widget/AssetAdditonalFieldsWidgetComponent.vue'
import DonutChartWidgetComponent from '@/components/equipment/generic/widget/DonutChartWidgetComponent.vue'
import { nextTick } from 'vue'
import ConsumptionHistogramChartWidgetComponent from '@/components/equipment/widget/ConsumptionHistogramChartWidgetComponent.vue'
import PublicPageWidgetComponent from '@/components/equipment/generic/widget/PublicPageWidgetComponent.vue'
import HiveHistoryWidgetComponent from '@/components/equipment/apiary/widget/HiveHistoryWidgetComponent.vue'
import HiveChartWidgetComponent from '@/components/equipment/apiary/widget/HiveChartWidgetComponent.vue'
import HiveInformationWidget from '@/components/equipment/apiary/widget/HiveInformationWidgetComponent.vue'
import BiodigestorCyclesWidgetComponent from '@/components/equipment/generic/widget/biodigestor/BiodigestorCyclesWidgetComponent.vue'
import BiodigestorMonitoringWidgetComponent from '@/components/equipment/generic/widget/biodigestor/BiodigestorMonitoringWidgetComponent.vue'
import BiodigestorHistoryDatatableWidgetComponent from '@/components/equipment/generic/widget/biodigestor/BiodigestorHistoryDatatableWidgetComponent.vue'
import AssetOperationsDatatableWidgetComponent from '@/components/equipment/generic/widget/AssetOperationsDatatableWidgetComponent.vue'
import BiodigestorGeneralViewWidgetComponent from '@/components/equipment/generic/widget/biodigestor/BiodigestorGeneralViewWidgetComponent.vue'
import FollowUpTimeLineWidgetComponent from '@/components/equipment/generic/widget/FollowUpTimeLineWidgetComponent.vue'
import { NoteTypeRelatable } from '@/models/INote'
import BiodigestorAssetSummaryCardWidgetComponent from '@/components/equipment/generic/widget/biodigestor/BiodigestorAssetSummaryCardWidgetComponent.vue'
import EconomyCo2WidgetComponent from '@/components/equipment/widget/EconomyCo2WidgetComponent.vue'

export default {
    name: 'AssetDetailComponent',
    components: {
        EconomyCo2WidgetComponent,
        BiodigestorAssetSummaryCardWidgetComponent,
        AssetOperationsDatatableWidgetComponent,
        BiodigestorHistoryDatatableWidgetComponent,
        BiodigestorMonitoringWidgetComponent,
        BiodigestorCyclesWidgetComponent,
        BiodigestorGeneralViewWidgetComponent,
        HiveInformationWidget,
        HiveChartWidgetComponent,
        HiveHistoryWidgetComponent,
        PublicPageWidgetComponent,
        ConsumptionHistogramChartWidgetComponent,
        DonutChartWidgetComponent,
        AssetAdditionalFieldsWidgetComponent,
        NoteDevicesWidgetComponent,
        AlertsWidgetComponent,
        AlertsConfiguredWidgetComponent,
        MapPositionHistoryWidgetComponent,
        DeviceTraitedDataByDeviceMetricDatatableWidgetComponent,
        DeviceTraitedDataByAssetMetricDatatableWidgetComponent,
        ConsumptionReportWidgetComponent,
        ChartByMetricsWidgetComponent,
        AssetModelSpecificationsWidgetComponent,
        AssetDevicesInformationWidgetComponent,
        AssetLocalisationWidgetComponent,
        BoosthermWidgetComponent,
        ChartByAssetWidgetComponent,
        InformationAssetWidgetComponent,
        ImageAssetWidgetComponent,
        FollowUpTimelineWidgetComponent: FollowUpTimeLineWidgetComponent,
        Container,
        Draggable,
    },
    props: {
        asset: {
            type: Object,
            required: true,
        },
        asset_module: {
            type: Object,
            required: true,
        },
    },
    computed: {
        NoteTypeRelatable() {
            return NoteTypeRelatable
        },
    },
    async created() {
        this.fetching_configuration = true
        if (this.asset?.devices) {
            //Fetch chart & metrics configuraiton
            await Promise.all([this.fetchAssetChartsConfiguration(), this.fetchAssetMetricsConfiguration()])
        }

        //Build widget organization object
        this.asset_module.sens_widgets_configuration.forEach((item) => {
            if (this.widgets[item.col - 1]) {
                this.widgets[item.col - 1].push(item)
            } else {
                this.widgets[item.col - 1] = [item]
            }
        })

        this.fetching_configuration = false
    },
    data() {
        return {
            fetching_configuration: true,
            is_asset_detail_visible: true,
            widgets: [],
            charts_configuration: [],
            metrics_configuration: [],
        }
    },
    methods: {
        onDrop(collection, { removedIndex, addedIndex, payload }) {
            if (removedIndex !== null) {
                this.widgets[collection].splice(removedIndex, 1)[0]
            }

            if (addedIndex !== null) {
                if (this.widgets[collection]) {
                    this.widgets[collection].splice(addedIndex, 0, payload)
                } else {
                    this.widgets[collection] = [payload]
                }
            }
        },

        getWidget(row) {
            return (index) => {
                return this.widgets[row][index]
            }
        },

        fetchAssetChartsConfiguration() {
            this.charts_configuration = []
            return Promise.all(
                this.asset.devices.map((device) => {
                    return Promise.resolve(this.fetchAssetChartsConfigurationByDevice(device))
                })
            ).catch((error) => {
                this.manageError(error)
            })
        },

        fetchAssetChartsConfigurationByDevice(device) {
            return DeviceRepository.getAssetChartsConfigurations(device.device_number).then((success) => {
                success.data.data.asset_charts_configurations.forEach((asset_charts_configuration) => {
                    this.charts_configuration.push(asset_charts_configuration)
                })
            })
        },

        fetchAssetMetricsConfiguration() {
            this.metrics_configuration = []
            return Promise.all(
                this.asset.devices.map((device) => {
                    return Promise.resolve(this.fetchAssetMetricsConfigurationByDevice(device))
                })
            ).catch((error) => {
                this.manageError(error)
            })
        },

        fetchAssetMetricsConfigurationByDevice(device) {
            return DeviceRepository.getAssetMetricsConfigurations(device.device_number).then((success) => {
                success.data.data.asset_metrics_configurations.forEach((asset_charts_configuration) => {
                    this.metrics_configuration.push(asset_charts_configuration)
                })
            })
        },

        getDeviceByChartConfigurationAndWidget(widget) {
            if (this.asset.devices) {
                return this.asset.devices.length === 1
                    ? this.asset.devices[0]
                    : this.asset.devices.find(
                          (device) =>
                              device.id_device ===
                              this.charts_configuration.find((config) => widget.widget_name.includes(config.widget_key))?.id_device
                      )
            }

            return null
        },

        getDeviceByMetricsConfigurationAndWidget(widget) {
            if (this.asset?.devices) {
                return this.asset.devices.length === 1
                    ? this.asset.devices[0]
                    : this.asset.devices.find(
                          (device) =>
                              device.id_device ===
                              this.metrics_configuration.find((config) => widget.widget_name.includes(config.widget_key))?.id_device
                      )
            }

            return null
        },

        getDeviceMapPositionHistoryWidget() {
            return this.asset?.devices?.find(this.isDeviceGeolocalisation) ?? this.asset?.devices[0]
        },
    },
    watch: {
        is_mobile() {
            this.is_asset_detail_visible = false
            nextTick(() => {
                this.is_asset_detail_visible = true
            })
        },
    },
}
</script>
